import { IWizardContext, IWizardStepState } from '@zendra/shared';
import { IStepDataItem } from '../interfaces/IStepDataItem';
import { IStepBasicMeta } from '../interfaces/IStepStandSelectionFilters';

const LAST_WIZARD_STEP_ID = 4;

export function cloneStepDataItems(
  data?: IStepDataItem[] | null,
): IStepDataItem[] {
  return (data ?? []).map(item => ({ ...item }));
}

export function buildStepDataSignature(
  data?: IStepDataItem[] | null,
): string {
  return JSON.stringify(cloneStepDataItems(data));
}

export function getPreviousStepData(context: IWizardContext): IStepDataItem[] {
  const previousStepId = context.activeStepId() - 1;
  return cloneStepDataItems(
    context.getStepState<IStepDataItem[]>(previousStepId)?.data,
  );
}

export function buildPreviousStepMeta(context: IWizardContext): IStepBasicMeta {
  return {
    previousStepSignature: buildStepDataSignature(getPreviousStepData(context)),
  };
}

export function shouldReuseCurrentStepData(
  state: IWizardStepState<IStepDataItem[]> | undefined,
  previousStepData: IStepDataItem[],
): boolean {
  const meta = state?.meta as IStepBasicMeta | undefined;

  return (
    state?.data !== undefined &&
    meta?.previousStepSignature === buildStepDataSignature(previousStepData)
  );
}

interface IPersistStepDataOptions<TMeta = unknown> {
  stepId?: number;
  data?: IStepDataItem[] | null;
  valid: boolean;
  completed?: boolean;
  dirty?: boolean;
  meta?: TMeta;
  invalidateNextSteps?: boolean;
}

export function persistStepData<TMeta = unknown>(
  context: IWizardContext,
  options: IPersistStepDataOptions<TMeta>,
): IStepDataItem[] {
  const stepId = options.stepId ?? context.activeStepId();
  const data = cloneStepDataItems(options.data);
  const currentState = context.getStepState<IStepDataItem[]>(stepId);
  const stepDataChanged =
    buildStepDataSignature(currentState?.data) !== buildStepDataSignature(data);

  if ((options.invalidateNextSteps ?? true) && stepDataChanged) {
    invalidateFollowingSteps(context, stepId);
  }

  context.patchStepState(stepId, {
    dirty: options.dirty ?? true,
    valid: options.valid,
    completed: options.completed,
    data,
    meta: options.meta,
  });

  return data;
}

export function invalidateFollowingSteps(
  context: IWizardContext,
  currentStepId: number,
  lastStepId = LAST_WIZARD_STEP_ID,
): void {
  for (let stepId = currentStepId + 1; stepId <= lastStepId; stepId++) {
    context.patchStepState(stepId, {
      dirty: false,
      valid: false,
      completed: false,
      data: undefined,
      meta: undefined,
      summary: undefined,
    });
  }
}

const APPLY_HERBICIDE_LABEL = 'APLICA HERBICIDA';

export function getSelectOptionLabel(
  options: Array<{ key: number; value: string }>,
  key?: number | null,
): string | undefined {
  if (key === null || key === undefined) {
    return undefined;
  }

  return options.find(option => option.key === key)?.value;
}

export function isApplyHerbicideSelected(
  stumpApplicationId?: number | null,
  stumpApplicationName?: string | null,
  options: Array<{ key: number; value: string }> = [],
): boolean {
  const label =
    getSelectOptionLabel(options, stumpApplicationId) ??
    stumpApplicationName ??
    '';

  return normalizeCatalogLabel(label) === APPLY_HERBICIDE_LABEL;
}

function normalizeCatalogLabel(value?: string | null): string {
  return (value ?? '').trim().replaceAll(/\s+/g, ' ').toUpperCase();
}
